import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConsulting } from '../../context/ConsultingContext';
import { useToast } from '../../context/ToastContext';
import { useResumeData } from '../../hooks/useResumeData';
import { Header } from '../../components/layout';
import ResumeRenderer from '../../components/resume/ResumeRenderer';
import ResumeEditForm from '../../components/resume/ResumeEditForm';
import { ProgressIndicator, useProgress, LatteButton, LatteSpinner, ConfirmationModal, ProgressGuideModal, MobileAlertModal, MobileConfirmationModal, ToolbarSeparator, ToolbarButton } from '../../components/common';
import { PortfolioGenerator } from '../../components/portfolio';
import { 
  MessageCircle, 
  Edit3, 
  Download, 
  FileText, 
  Check, 
  X,
  Globe,
  Trash2,
  Eye,
  Loader2,
  Languages,
  Share2,
  Briefcase,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { downloadResumeAsPDF } from '../../utils/pdfGenerator';
import { generateEnglishVersion } from '../../utils/englishVersion/englishVersionService';
import { trackEvent } from '../../utils/analytics';
import { analyzeResumeGaps } from '../../utils/progressSync';
import '../../styles/mobile/mobile-resume.css';

const MobileResumePage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { currentSession, extractedResumeData } = useConsulting();
  const { showSuccess, showError, showInfo } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 데스크탑과 동일한 useResumeData 훅 사용
  const {
    resumeData,
    sessionData,
    loading,
    existingPortfolio,
    portfolioLoading,
    updateResumeData,
    saveResumeData,
    isResumeComplete,
    setExistingPortfolio,
    refreshResumeData,
    englishUpdateNeeded,
    englishUpdateInfo,
    checkEnglishUpdateStatus,
    modifiedFields,
    setModifiedFields,
    setLastSyncedData
  } = useResumeData(id, user, currentSession, extractedResumeData, showError, showSuccess);

  // 로컬 상태
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'edit'
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const [isGeneratingEnglish, setIsGeneratingEnglish] = useState(false);
  const [englishGenerationError, setEnglishGenerationError] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('kr');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false); // 빠른 액션 메뉴 상태 추가
  const [progressModalConfig, setProgressModalConfig] = useState({
    isOpen: false,
    featureName: ''
  });
  const [showMobileDownloadModal, setShowMobileDownloadModal] = useState(false); // PDF 다운로드 모달 상태
  const [scaledHeight, setScaledHeight] = useState(null); // 스케일된 높이 저장
  const [selectedTemplateId, setSelectedTemplateId] = useState(1); // 선택된 템플릿 ID 추적

  const resumeTemplateRef = useRef(null);
  const resumeWrapperRef = useRef(null); // wrapper ref 추가
  const actualProgress = useProgress(resumeData);
  const fromConsulting = searchParams.get('from') === 'consulting';
  const sessionId = id || currentSession?.id;

  // 진행률에 따른 텍스트 색상 클래스 반환
  const getProgressTextClass = (progress) => {
    if (progress === 100) return 'progress-complete';
    if (progress >= 80) return 'progress-high';
    if (progress >= 20) return 'progress-active';
    return 'progress-initial';
  };

  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}. ${month}. ${day}.`;
  };

  // 핸들러 함수들
  const handleStartEditing = () => {
    setActiveTab('edit');
    trackEvent.startEditingResume(sessionId);
  };

  const handleFinishEditing = async (shouldSave = true) => {
    try {
      if (shouldSave) {
        await saveResumeData();
        showSuccess('이력서가 저장되었습니다!', 1000);
        
        const editedFields = Array.from(modifiedFields);
        trackEvent.saveResumeEdit(sessionId, editedFields);
        
        if (sessionId) {
          setTimeout(async () => {
            await checkEnglishUpdateStatus(sessionId);
          }, 500);
        }
      } else {
        trackEvent.cancelResumeEdit(sessionId);
      }
      setActiveTab('preview');
    } catch (error) {
      showError('저장 중 오류가 발생했습니다.', 1000);
    }
  };

  const handleContinueConsulting = () => {
    if (!sessionId) {
      showError('AI 컨설팅을 시작할 세션 정보가 없습니다.');
      return;
    }
    navigate(`/consulting?session=${sessionId}`);
  };

  const handleDownload = async () => {
    // 진행률 체크
    if (actualProgress !== 100) {
      setProgressModalConfig({
        isOpen: true,
        featureName: 'PDF 다운로드'
      });
      return;
    }
    
    // 모바일에서는 PDF 다운로드 기능을 사용할 수 없음 - 모달로 표시
    setShowMobileDownloadModal(true);
    return;
    
    // 아래 코드는 실행되지 않지만 참고용으로 남겨둠
    setIsDownloading(true);

    try {
      if (!resumeData) {
        throw new Error('이력서 데이터가 없습니다.');
      }
      
      const trackingData = {
        userId: user?.id,
        sessionId: sessionId,
        resumeId: resumeData.id || sessionId,
        language: currentLanguage,
        templateId: selectedTemplateId
      };
      
      const result = await downloadResumeAsPDF(resumeData, trackingData);
      
      if (result) {
        console.log(`✅ ${currentLanguage === 'en' ? '영어' : '한국어'} 버전 PDF 다운로드 완료`);
        trackEvent.downloadDocument('resume', currentLanguage === 'en' ? 'pdf_en' : 'pdf_kr');
      } else {
        console.log('📝 사용자가 PDF 다운로드를 취소했습니다.');
      }
    } catch (error) {
      console.error('❌ PDF 생성 중 오류 발생:', error);
      showError(`PDF 생성 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = () => {
    if (!sessionId || !user?.id) {
      showError('삭제할 세션 정보가 없습니다.');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const { error: deleteError, count: updatedCount } = await supabase
        .from('consulting_sessions')
        .update({ 
          deleted_at: new Date().toISOString(),
          status: 'abandoned'
        }, { count: 'exact' })
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .is('deleted_at', null);
        
      if (deleteError) {
        showError('삭제 중 오류가 발생했습니다: ' + deleteError.message);
        return;
      }
      
      if (updatedCount === 0) {
        showError('세션 삭제에 실패했습니다. 이미 삭제되었거나 권한이 없습니다.');
        return;
      }
      
      showSuccess('이력서가 삭제되었습니다.');
      navigate('/documents');
      
    } catch (error) {
      console.error('❌ 삭제 중 예외:', error);
      showError('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleGenerateEnglishVersion = async () => {
    if (!resumeData || !sessionId) {
      return;
    }

    setIsGeneratingEnglish(true);
    setEnglishGenerationError(false);
    
    try {
      const fieldsToTranslate = modifiedFields.size > 0 ? modifiedFields : null;
      
      const englishResumeJSON = await generateEnglishVersion(
        resumeData, 
        sessionId, 
        fieldsToTranslate
      );
      
      updateResumeData({
        ...resumeData,
        english_version: englishResumeJSON
      });
      
      trackEvent.generateEnglishResume(sessionId);
      
      setModifiedFields(new Set());
      setLastSyncedData(resumeData);
      
      await checkEnglishUpdateStatus(sessionId);
      
    } catch (error) {
      console.error('❌ 영어 버전 생성 실패:', error);
      
      if (error.message.includes('영어 이력서 생성 중 오류가 발생했습니다')) {
        setEnglishGenerationError(true);
      }
      
      showError(error.message);
    } finally {
      setIsGeneratingEnglish(false);
    }
  };

  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    trackEvent.switchLanguage(sessionId, language);
    
    if (language === 'en' && resumeData?.english_version) {
      trackEvent.viewEnglishResume(sessionId);
    }
  };

  // 스케일된 높이 계산
  useEffect(() => {

    const calculateScaledHeight = () => {
      // resumeWrapperRef가 null인지 체크
      if (!resumeWrapperRef.current) return;
      
      // .resume-renderer-content 요소 찾기
      const resumeContent = resumeWrapperRef.current.querySelector('.resume-renderer-content');
      
      // resumeContent가 없으면 종료
      if (!resumeContent) return;

      // 현재 화면 너비에 따른 스케일 값 결정
      const screenWidth = window.innerWidth;
      let scale = 0.45; // 기본값
      
      if (screenWidth <= 390) {
        scale = 0.42;
      } else if (screenWidth <= 430) {
        scale = 0.48;
      } else if (screenWidth <= 768) {
        scale = 0.52;
      }

      // getBoundingClientRect()로 실제 렌더링된 크기 가져오기
      const rect = resumeContent.getBoundingClientRect();
      const actualHeight = rect.height;
      
      // 원본 높이도 확인 (비교용)
      const originalHeight = resumeContent.scrollHeight;
      
      // wrapper의 패딩 계산
      const wrapperPadding = 48; // 상단 1rem + 하단 2rem = 3rem = 48px
      
      // 최종 높이
      const calculatedHeight = actualHeight + wrapperPadding;
      
      setScaledHeight(calculatedHeight);
      
      
      // wrapper의 현재 높이 확인
      setTimeout(() => {
        if (resumeWrapperRef.current) {
          
          // 흰색 컨테이너 높이 확인 - CSS 선택자로 정확히 찾기
          const allDivs = resumeWrapperRef.current.querySelectorAll('div');
          allDivs.forEach((div, index) => {
            const styles = window.getComputedStyle(div);
          });
          
          // 실제 이력서 컨텐츠 높이
          const resumeRenderer = resumeWrapperRef.current.querySelector('.mobile-resume-renderer');
        }
      }, 100);
    };

    // 초기 계산
    setTimeout(calculateScaledHeight, 500); // DOM 렌더링 대기
    
    // 추가로 1초 후에도 한 번 더 계산 (안전장치)
    setTimeout(calculateScaledHeight, 1000);

    // 화면 크기 변경 시 재계산
    window.addEventListener('resize', calculateScaledHeight);
    
    return () => {
      window.removeEventListener('resize', calculateScaledHeight);
    };
  }, [resumeData, currentLanguage, resumeWrapperRef.current]); // ref도 의존성에 추가

  // 로딩 상태
  if (loading) {
    return (
      <div className="mobile-page-container">
        <Header title="이력서 보기" />
        <div className="flex-col flex-center p-12">
          <LatteSpinner size="lg" className="mb-4" />
          <p className="text-gray-600">이력서 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page-container">
      {/* sticky 컨테이너로 모든 고정 요소를 감싸기 */}
      <div className="mobile-sticky-container">
        <Header title="이력서" />
        
        {/* 상단 정보 영역 - 데스크탑과 동일한 스타일 */}
        <div className="mobile-resume-header">
          <div className="section-container py-3">
            <div className="flex-column gap-3">
              <div>
                <h2 className="mobile-resume-header-title">
                  <span className="mobile-resume-header-name">
                    {(() => {
                      const name = sessionData?.name || resumeData.name || '이름 없음';
                      // 이름에서 _YYYY-MM-DD 패턴 제거
                      return name.replace(/_\d{4}-\d{2}-\d{2}$/, '');
                    })()}
                  </span>
                  <span className="mobile-resume-header-suffix">
                    님의 이력서
                  </span>
                </h2>
                <div className="mobile-resume-header-info">
                  <div className="mobile-resume-header-meta">
                    <span>
                      <span className="mobile-resume-header-label">생성일:</span>
                      <span className="mobile-resume-header-value">{formatDate(sessionData?.created_at)}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 - 데스크탑 스타일 적용 */}
        <div className="mobile-tab-navigation">
          <div className="tab-container">
            <button
              className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
              onClick={() => setActiveTab('preview')}
            >
              <Eye size={18} />
              <span>미리보기</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={() => setActiveTab('edit')}
            >
              <Edit3 size={18} />
              <span>편집하기</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* 모바일 진행률 표시 - MobileConsultingPage와 동일 */}
      <button
        className="mobile-progress-floating"
        onClick={() => {
          if (fromConsulting) {
            // 컨설팅에서 왔으면 컨설팅 화면으로 돌아가기
            navigate(`/consulting?session=${sessionId}`);
          } else {
            // 아니면 기존처럼 퀵 액션 메뉴 표시
            setShowQuickActions(!showQuickActions);
          }
        }}
        title={fromConsulting ? "컨설팅 화면으로 돌아가기" : "진행률 및 빠른 메뉴"}
      >
        <div className="floating-button-content">
          <span className={`progress-text ${getProgressTextClass(actualProgress)}`}>
            {actualProgress}%
          </span>
          {fromConsulting && <MessageCircle size={16} className="floating-button-icon" />}
        </div>
      </button>

      {/* 메인 컨텐츠 영역 */}
      <div className="mobile-resume-content">
        {/* 미리보기 탭 */}
        {activeTab === 'preview' && (
          <div className="preview-tab-content">
            {/* 툴바 영역 - 데스크톱 ResumeToolbar 스타일 참조 */}
            <div className="mobile-toolbar">
              <div className="toolbar-section">

                {/* 액션 버튼들 - 데스크톱과 동일한 툴바 스타일 */}
                <div className="toolbar-buttons" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                  {/* 언어 선택 - 영어 버전이 있을 때만 */}
                  {resumeData?.english_version && (
                    <div className="toolbar-language-selector">
                      <button
                        onClick={() => handleLanguageChange('kr')}
                        className={`toolbar-language-button ${currentLanguage === 'kr' ? 'active' : ''}`}
                        title="한국어 버전 보기"
                      >
                        <div className={`toolbar-language-radio ${currentLanguage === 'kr' ? 'active' : ''}`}>
                          {currentLanguage === 'kr' && (
                            <div className="toolbar-language-radio-dot"></div>
                          )}
                        </div>
                        <span>KR</span>
                      </button>
                      
                      <button
                        onClick={async () => {
                          // 먼저 언어를 English로 변경 (즉시 라디오 버튼 선택 효과)
                          handleLanguageChange('en');
                          
                          // 업데이트가 필요하거나 에러 상태인 경우에만 동기화 실행
                          if ((englishUpdateNeeded || englishGenerationError) && !isGeneratingEnglish && actualProgress === 100) {
                            handleGenerateEnglishVersion();
                          }
                        }}
                        disabled={isGeneratingEnglish}
                        className={`toolbar-language-button ${currentLanguage === 'en' ? 'active' : ''} ${isGeneratingEnglish ? 'disabled' : ''}`}
                        title={
                          isGeneratingEnglish
                          ? '영어 버전 동기화 중...'
                          : 'English Version 보기'
                        }
                      >
                        <div className={`toolbar-language-radio ${currentLanguage === 'en' ? 'active' : ''}`}>
                          {currentLanguage === 'en' && (
                            <div className="toolbar-language-radio-dot"></div>
                          )}
                        </div>
                        
                        {isGeneratingEnglish ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                          </>
                        ) : (
                          <>
                            {englishGenerationError ? (
                              <RefreshCw className="w-3 h-3" />
                            ) : null}
                            <span>EN</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {/* 영어 이력서 만들기 - 영어 버전이 없을 때만 */}
                  {!resumeData?.english_version && (
                    <ToolbarButton
                      onClick={() => {
                        if (actualProgress !== 100) {
                          setProgressModalConfig({
                            isOpen: true,
                            featureName: '영어 버전 생성'
                          });
                        } else {
                          handleGenerateEnglishVersion();
                        }
                      }}
                      variant="default"
                      disabled={isGeneratingEnglish}
                      title="영어 이력서 만들기"
                    >
                      {isGeneratingEnglish ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : englishGenerationError ? (
                        <RefreshCw className="w-4 h-4" />
                      ) : (
                        <span className="toolbar-button-icon">🌍</span>
                      )}
                      <span className="hidden xl:inline">
                        {isGeneratingEnglish ? '생성 중...' : englishGenerationError ? '재시도하기' : '영어 이력서 만들기'}
                      </span>
                    </ToolbarButton>
                  )}
                  
                  {/* PDF 다운로드 */}
                  <ToolbarButton
                    onClick={handleDownload}
                    variant="default"
                    disabled={isDownloading}
                    title="PDF 다운로드"
                  >
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <span className="toolbar-button-icon">📥</span>
                    )}
                    <span className="hidden xl:inline">PDF 다운로드</span>
                  </ToolbarButton>
                  
                  {/* 내 웹페이지 만들기/보기 */}
                  {existingPortfolio ? (
                    <ToolbarButton
                      onClick={() => window.open(`/portfolio/${existingPortfolio.slug}`, '_blank')}
                      variant="default"
                      title="내 웹페이지 보기"
                    >
                      <span className="toolbar-button-icon">🚀</span>
                      <span className="hidden xl:inline">내 웹페이지 가기</span>
                    </ToolbarButton>
                  ) : (
                    <ToolbarButton
                      onClick={() => {
                        if (actualProgress !== 100) {
                          setProgressModalConfig({
                            isOpen: true,
                            featureName: '웹페이지 생성'
                          });
                        } else {
                          setIsPortfolioModalOpen(true);
                        }
                      }}
                      variant="default"
                      title="내 웹페이지 만들기"
                    >
                      <span className="toolbar-button-icon">💎</span>
                      <span className="hidden xl:inline">내 웹페이지 만들기</span>
                    </ToolbarButton>
                  )}
                  </div>
                  
                  {/* 삭제 - 우측 정렬 */}
                  <button
                    onClick={handleDelete}
                    className="toolbar-delete-button ml-auto"
                    title="삭제"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* 데스크탑 기능 안내 */}
            <div className="mobile-desktop-notice">
              <div className="notice-content">
                <div className="notice-icon">💻</div>
                <div className="notice-text">
                  <p className="notice-title">데스크탑에서 더 많은 기능을 사용하세요!</p>
                  <p className="notice-description">
                    PC에서 접속하시면 다양한 프리미엄 템플릿과 PDF 다운로드 기능을 이용할 수 있습니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 이력서 렌더러 */}
            <div 
              ref={resumeWrapperRef}
              className="mobile-resume-renderer-wrapper"
              style={{ 
                paddingBottom: '1rem',
                height: scaledHeight ? `${scaledHeight}px` : 'auto', // 계산된 높이 적용
                transition: 'height 0.3s ease' // 부드러운 전환
              }}
            >
              <ResumeRenderer
                resumeData={currentLanguage === 'en' && resumeData?.english_version ? 
                  {
                    ...resumeData,
                    name: resumeData?.name_en || resumeData?.english_version?.name || resumeData.name,
                    title: resumeData?.english_version?.title || resumeData.title,
                    summary: resumeData?.english_version?.summary || resumeData.summary,
                    keyStrengths: resumeData?.english_version?.key_strengths || resumeData.keyStrengths,
                    experiences: resumeData?.english_version?.experiences || resumeData.experiences,
                    education: resumeData?.english_version?.education || resumeData.education,
                    skills: resumeData?.english_version?.skills || resumeData.skills,
                    certificates: resumeData?.english_version?.certificates || resumeData.certificates,
                    papers: resumeData?.english_version?.papers || resumeData.papers,
                  } : resumeData
                }
                updatingField={null}
                containerMode="responsive"
                enableAutoScale={true}
                onContainerRef={(ref) => { resumeTemplateRef.current = ref; }}
                language={currentLanguage}
                showTemplateSelector={false}
                className="mobile-resume-renderer"
                style={{ 
                  pointerEvents: 'none', // 이력서 내부 요소들의 클릭 이벤트 방지
                  minHeight: 'auto' // A4 고정 높이 오버라이드
                }}
                selectedTemplate={selectedTemplateId}
                onTemplateChange={setSelectedTemplateId}
              />
            </div>
          </div>
        )}

        {/* 편집 탭 */}
        {activeTab === 'edit' && (
          <div className="edit-tab-content">
            <ResumeEditForm
              resumeData={resumeData}
              onUpdate={updateResumeData}
              onSave={saveResumeData}
              onCancel={(wasSaved) => handleFinishEditing(wasSaved)}
              isMobile={true}
            />
          </div>
        )}
      </div>



      {/* 포트폴리오 생성 모달 */}
      <PortfolioGenerator 
        userId={user?.id}
        sessionId={sessionId}
        resumeData={resumeData}
        externalModalOpen={isPortfolioModalOpen}
        onModalClose={() => setIsPortfolioModalOpen(false)}
        onPortfolioCreated={(portfolio) => {
          setExistingPortfolio(portfolio);
          setIsPortfolioModalOpen(false);
          showSuccess('포트폴리오가 생성되었습니다!');
        }}
      />
      
      {/* 삭제 확인 모달 - 모바일용 */}
      <MobileConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="이력서 삭제"
        message={`"${(() => {
          const name = sessionData?.name || resumeData?.name || '이름 없음';
          // 이름에서 _YYYY-MM-DD 패턴 제거
          return name.replace(/_\d{4}-\d{2}-\d{2}$/, '');
        })()} 님의 이력서"를 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없으며, 관련된 모든 데이터가 함께 삭제됩니다.`}
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />
      
      {/* 진행률 안내 모달 */}
      <ProgressGuideModal
        isOpen={progressModalConfig.isOpen}
        onClose={() => setProgressModalConfig({ isOpen: false, featureName: '' })}
        actualProgress={actualProgress}
        resumeData={resumeData}
        featureName={progressModalConfig.featureName}
        onContinueConsulting={handleContinueConsulting}
        onEdit={handleStartEditing}
      />
      
      {/* 모바일 PDF 다운로드 안내 모달 */}
      <MobileAlertModal
        isOpen={showMobileDownloadModal}
        onClose={() => setShowMobileDownloadModal(false)}
        title="PDF 다운로드 안내"
        message="PDF 다운로드는 데스크탑에서만 사용하실 수 있습니다. 데스크탑으로 접속하시면 다양한 프리미엄 이력서 템플릿도 만나보실 수 있습니다."
        type="info"
        confirmText="확인"
      />
      
      {/* 빠른 액션 메뉴 - MobileConsultingPage와 동일 */}
      {showQuickActions && (
        <div className="quick-actions-overlay" onClick={() => setShowQuickActions(false)}>
          <div className="quick-actions-menu" onClick={e => e.stopPropagation()}>
            <h3 className="menu-title">진행 상황</h3>
            <div className="progress-details">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${actualProgress}%` }}>
                  {actualProgress >= 50 && (
                    <span className="progress-label">{actualProgress}%</span>
                  )}
                </div>
              </div>
              <div className="progress-info-row">
                <span className="progress-text">
                  {actualProgress === 100 ? '이력서가 완성되었습니다!' : '이력서 작성 중'}
                </span>
                <span className="progress-value">{actualProgress}%</span>
              </div>
            </div>
            
            <div className="progress-items">
              {/* 진행률 상세 표시 */}
              <div className="progress-item">
                <span className="item-name">기본 정보</span>
                {resumeData?.name && resumeData?.email && resumeData?.phone ? (
                  <Check size={20} className="completed" />
                ) : (
                  <Check size={20} className="pending" />
                )}
              </div>
              <div className="progress-item">
                <span className="item-name">자기소개</span>
                {resumeData?.summary && resumeData.summary.length > 50 ? (
                  <Check size={20} className="completed" />
                ) : (
                  <Check size={20} className="pending" />
                )}
              </div>
              <div className="progress-item">
                <span className="item-name">핵심 역량</span>
                {resumeData?.keyStrengths && resumeData.keyStrengths.length > 0 ? (
                  <Check size={20} className="completed" />
                ) : (
                  <Check size={20} className="pending" />
                )}
              </div>
              <div className="progress-item">
                <span className="item-name">경력 사항</span>
                {resumeData?.experiences && resumeData.experiences.length > 0 ? (
                  <Check size={20} className="completed" />
                ) : (
                  <Check size={20} className="pending" />
                )}
              </div>
              <div className="progress-item">
                <span className="item-name">학력 사항</span>
                {resumeData?.education && resumeData.education.length > 0 ? (
                  <Check size={20} className="completed" />
                ) : (
                  <Check size={20} className="pending" />
                )}
              </div>
              <div className="progress-item">
                <span className="item-name">보유 스킬</span>
                {resumeData?.skills && resumeData.skills.length > 0 ? (
                  <Check size={20} className="completed" />
                ) : (
                  <Check size={20} className="pending" />
                )}
              </div>
            </div>
            
            <button 
              className="action-button primary"
              onClick={() => {
                setShowQuickActions(false);
                handleContinueConsulting();
              }}
            >
              AI 컨설팅 계속하기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileResumePage;
